import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { useItemModel, useModelMutations, type ItemModel, type Specification } from '@/api/capacities';
import { ModelFormFields, type ModelFieldSetter, type ModelFormValues } from './model-form-fields';
import { ModelSpecificationsEditor } from './model-specifications-editor';

interface FormState extends ModelFormValues {
  specifications: Specification[];
}

const EMPTY: FormState = {
  item_type: 'equipment',
  category: '',
  model: '',
  name: '',
  description: '',
  is_active: true,
  specifications: [],
};

const toForm = (m: ItemModel): FormState => ({
  item_type: m.item_type ?? 'equipment',
  category: m.category ?? '',
  model: m.model ?? '',
  name: m.name ?? '',
  description: m.description ?? '',
  is_active: m.is_active ?? true,
  specifications: m.specifications ?? [],
});

/**
 * Item Model create / edit form (`/capacities/models/new` and `/:id/edit`).
 * Scalar fields (type · category · model · name · description · active) plus a
 * dynamic Specifications sub-editor. Wired to `useModelMutations` for real
 * create/update — live execution is performed by the user.
 */
export default function CapacityModelFormPage() {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id ?? '';
  const isEdit = !!id;

  const { data: model, isLoading } = useItemModel(id);
  const { create, update } = useModelMutations();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (model) setForm(toForm(model));
  }, [model]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Narrowly-typed adapter for the scalar-field block. Its keys are a subset of
  // FormState's with identical value types, so the cast is sound; TS just can't
  // prove the equality through the generic bound.
  const setField: ModelFieldSetter = (key, value) =>
    set(key, value as FormState[typeof key]);

  const invalidFields = {
    model: form.model.trim() === '',
    name: form.name.trim() === '',
    category: form.category.trim() === '',
  };
  const invalid = invalidFields.model || invalidFields.name || invalidFields.category;
  const submitting = create.isPending || update.isPending;
  // Validate-on-touch: surface "Required" only after blur or a submit attempt.
  const [touched, setTouched] = useState({ model: false, name: false, category: false });
  const [submitted, setSubmitted] = useState(false);
  const touch = (key: keyof typeof touched) => setTouched((t) => ({ ...t, [key]: true }));
  const errors = {
    model: (touched.model || submitted) && invalidFields.model,
    name: (touched.name || submitted) && invalidFields.name,
    category: (touched.category || submitted) && invalidFields.category,
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (invalid) {
      setError('Model, name and category are required.');
      return;
    }
    setError(null);
    // Drop blank spec rows (name+value both empty) so we never POST placeholders.
    const specifications = form.specifications
      .filter((s) => s.name.trim() !== '' || s.value.trim() !== '')
      .map((s) => ({
        name: s.name.trim(),
        value: s.value.trim(),
        unit: s.unit?.trim() || undefined,
        is_primary: !!s.is_primary,
      }));
    const body: Partial<ItemModel> = {
      item_type: form.item_type,
      category: form.category.trim(),
      model: form.model.trim(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      is_active: form.is_active,
      specifications,
    };
    const opts = {
      onSuccess: () => navigate(isEdit ? `/capacities/models/${id}` : '/capacities/models'),
      onError: () => setError('Failed to save the model. Please try again.'),
    };
    if (isEdit) update.mutate({ id, body }, opts);
    else create.mutate(body, opts);
  };

  const cancelTo = isEdit ? `/capacities/models/${id}` : '/capacities/models';

  return (
    <Container size="full">
      <div style={{ marginBottom: 'var(--vn-space-4)' }}>
        <Button asChild variant="ghost" size="sm">
          <button type="button" onClick={() => navigate(cancelTo)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              <ArrowLeft size={16} />
              Cancel
            </span>
          </button>
        </Button>
      </div>

      <PageHeader
        title={isEdit ? 'Edit model' : 'New model'}
        description="Item models define the equipment and component catalog."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      {isEdit && isLoading ? (
        <TabLoading rows={5} />
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Card flat>
              <CardBody>
                <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
                  {error ? <Alert tone="danger" title={error} /> : null}
                  <ModelFormFields values={form} onChange={setField} errors={errors} onTouch={touch} />
                </div>
              </CardBody>
            </Card>

            <Card flat>
              <CardHeader>
                <CardTitle>Specifications</CardTitle>
              </CardHeader>
              <CardBody>
                <ModelSpecificationsEditor
                  specs={form.specifications}
                  onChange={(specifications) => set('specifications', specifications)}
                />
              </CardBody>
              <CardFooter style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
                <Button type="button" variant="ghost" onClick={() => navigate(cancelTo)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
                  {isEdit ? 'Save changes' : 'Create model'}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </form>
      )}
    </Container>
  );
}
